import type { FeedItem, PostResult } from "../queries.ts";

export interface ThreadPreview {
  readonly key: string;
  readonly item: FeedItem;
  readonly ancestors: readonly PostResult[];
  readonly gap: boolean;
}

export async function threadPreviews(
  feed: readonly FeedItem[],
  seen: ReadonlySet<string>,
  loadPosts: (uris: readonly `at://${string}`[]) => Promise<readonly PostResult[]>,
  context: "threads" | "parents" = "threads",
): Promise<ThreadPreview[]> {
  const selected = new Set(seen);
  const posts = new Map<string, PostResult>(feed.map(({ post }) => [post.uri, {
    $type: "blue.cerulea.app.views#availablePost",
    post,
  }]));
  const pending: { key: string; item: FeedItem; uris: `at://${string}`[] }[] = [];
  for (const item of feed) {
    const { post } = item;
    const reply = !item.repostedBy && post.record.reply;
    const ownPost = `at://${post.author.did}/blue.cerulea.app.post/`;
    const selfReply = reply && reply.parent.uri.startsWith(ownPost);
    const ownRoot = selfReply && reply.root.uri.startsWith(ownPost);
    const key = context === "threads" && ownRoot ? reply.root.uri : item.uri;
    if (selected.has(key)) continue;
    selected.add(key);
    const uris = [
      ...new Set(
        context === "parents"
          ? reply ? [reply.parent.uri] : []
          : selfReply
          ? [...(ownRoot ? [reply.root.uri] : []), reply.parent.uri]
          : [],
      ),
    ].filter((uri) => uri !== post.uri);
    pending.push({ key, item, uris });
  }

  // Root + parent keeps previews bounded, even when a thread spans many feed pages.
  const missing = [...new Set(pending.flatMap(({ uris }) => uris))]
    .filter((uri) => !posts.has(uri));
  for (let i = 0; i < missing.length; i += 25) {
    for (const result of await loadPosts(missing.slice(i, i + 25))) {
      posts.set(
        result.$type === "blue.cerulea.app.views#availablePost" ? result.post.uri : result.uri,
        result,
      );
    }
  }
  return pending.map(({ key, item, uris }) => {
    const ancestors = uris.map((uri): PostResult =>
      posts.get(uri) ?? {
        $type: "blue.cerulea.app.views#unavailablePost",
        uri,
        reason: "notFound",
      }
    );
    const parent = ancestors.at(-1);
    const gap = ancestors.length === 2 &&
      (parent?.$type !== "blue.cerulea.app.views#availablePost" ||
        parent.post.record.reply?.parent.uri !== uris[0]);
    return { key, item, ancestors, gap };
  });
}
